/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import guidebookContentStyle from "../guidebookContentStyle";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Haertefallscheidung_700px.jpg";
import { gaSet } from "../../../services/cookieService";
import Typography from "@mui/material/Typography";
import { Link as MuiLink, Paper } from "@mui/material";
import MuiListNativeStyled from "../../List/MuiListNativeStyled";

const GuidebookHardshipDivorce = () => {
  const title = "Ratgeber - Härtefallscheidung | Legalbird";
  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content="Wann eine Härtefallscheidung durchgeführt werden kann, erfahren Sie hier kurz zusammengefasst." />
      </Helmet>

      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan slogan={"Einfach erklärt: Härtefallscheidung"} backgroundImagePath={backgroundTeaser} height={300} headingColor={"#333333"} />
        <TextContainer>
          <p>
            Eine Scheidung ist allgemein erst möglich, wenn die Ehepartner ein Jahr voneinander getrennt leben. In Einzelfällen kann eine Scheidung jedoch auch
            früher durchgeführt werden. Hierfür muss ein sogenannter Härtefall vorliegen. Wann eine Härtefallscheidung durchgeführt werden kann, erfahren Sie
            hier kurz zusammengefasst.
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Vier Arten von Scheidungen
          </Typography>
          <p>Bei einer Scheidung wird zwischen vier Fällen unterschieden, die sich durch die Situation zwischen den Ex-Partnern unterscheiden:</p>
          <MuiListNativeStyled
            sx={{ listStyle: "decimal" }}
            listItems={[
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/einvernehmliche-scheidung"}>
                Einvernehmliche Scheidung
              </MuiLink>,
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/streitige-scheidung"}>
                Streitige Scheidung
              </MuiLink>,
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/scheidung-drei-jahre-trennung"}>
                Partner leben länger als 3 Jahre getrennt
              </MuiLink>,
              "Härtefallscheidung",
            ]}
          />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Was ist eine Härtefallscheidung?
          </Typography>
          <p>
            Sie möchten sich frühzeitig scheiden lassen ohne das Trennungsjahr abzuwarten? Auch wenn beide Ehepartner die Scheidung wollen, ist eine Scheidung
            bei einer Trennungszeit von weniger als einem Jahr nur möglich, wenn ein Härtefall vorliegt. Die Härtefallscheidung ermöglicht eine vorzeitige
            Scheidung, wenn das Trennungsjahr eine unzumutbare Härte für einen der Ehepartner darstellt. Wichtig zu wissen: Eine kurze Ehe ist kein Härtefall.
            Auch bei einer kurzen Ehe ist das Trennungsjahr verpflichtende Voraussetzung für eine Scheidung.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Voraussetzungen für eine Härtefallscheidung
          </Typography>
          <p>
            Härtefallscheidungen sind Ausnahmefälle, die nur in besonderen Situationen durchgeführt werden. Es gibt für Härtefallscheidungen keine pauschal
            verbindlichen Richtlinien und die Urteile sind Einzelfallentscheidungen.
          </p>
          <Typography sx={guidebookContentStyle.listIntroText}>Allgemein gelten jedoch folgende Voraussetzungen:</Typography>
          <MuiListNativeStyled
            sx={guidebookContentStyle.bulletList}
            listItems={[
              "Die Ehe muss gescheitert sein",
              "Die Ehepartner leben räumlich getrennt",
              "Der Grund für die frühzeitige Scheidung muss immer in der Person des Ehepartners liegen",
              "Der Grund muss dem Gericht im Detail geschildert und bewiesen werden",
            ]}
          />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Beispiele für Härtefälle
          </Typography>
          <Typography sx={guidebookContentStyle.listIntroText}>Ein Härtefall kann vorliegen, wenn</Typography>
          <MuiListNativeStyled
            sx={guidebookContentStyle.bulletList}
            listItems={[
              "der Antragsteller vom Partner misshandelt wird",
              "der Partner mit jemand anderem ein Kind erwartet",
              "der Partner Alkoholiker ist und seinen Alkoholismus offen auslebt",
              "der Partner drogenabhängig ist",
              "der Partner sich nach der Trennung prostituiert",
            ]}
          />
          <p> Allgemein gilt: Das Gericht entscheidet immer nach Einzelfall und der Antragsteller muss den Härtefall beweisen.</p>
          <p>
            <strong>TIPP:</strong> Eine Härtefallscheidung zu erwirken kann sehr kompliziert sein. Oftmals reicht jedoch auch eine räumliche Trennung aus, um das
            Trennungsjahr zu überbrücken und die größten Probleme zu lösen. Nach einem Jahr kann die Scheidung normal eingereicht und durchgeführt werden. Es
            handelt sich dann - je nach Einigkeit zwischen den Ehepartnern - um eine{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/einvernehmliche-scheidung"}>
              einvernehmliche Scheidung
            </MuiLink>{" "}
            oder{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/streitige-scheidung"}>
              streitige Scheidung
            </MuiLink>
            .
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Ihre Scheidung
          </Typography>
          <p>
            Sie sind sich nicht sicher, um welche Art von Scheidung es sich bei Ihnen handelt? Sie können trotzdem jetzt mit Ihrer Scheidung starten, indem Sie
            ganz einfach alle Angaben für den Antrag direkt hier online eingeben - wir helfen weiter und begleiten Sie professionell und persönlich bei Ihrer
            Scheidung.
          </p>
          <DoubleButton buttonText={"zum Scheidungsantrag"} buttonLink={"/servicewelt"} backText={"Zurück zum Ratgeber"} backLink={"/ratgeber/familienrecht"} />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookHardshipDivorce;
